import { useCurrentDaycareInfos } from '@/hooks/useCurrentDaycareInfos'
import { EventCalendar } from '@/types/Calendar'
import { ChildWithParents } from '@/types/Child'
import { OptionsGroup, SelectOptions } from '@/types/Select'
import { OccasionalSlot } from '@/types/Slots'
import { useTranslation } from 'react-i18next'
import { isChildInSection } from '../functions/isChildInSection'
import { hasConflictingSlot } from '../functions/hasConflictingSlot'
import { toChildSelectOption } from '../functions/toChildSelectOption'

export enum PriorityGroupForChildren {
    PRIMARY_GROUP_CHILD = 'priorityChilds',
    NOT_PRIMARY_GROUP_CHILD = 'notPrimaryGroupChild'
}

export function useGetOptionChilds({
    childs,
    occasionalSlot,
    selectedSectionId,
    calendarEvents,
    date,
    startTime,
    endTime
}: {
    childs: ChildWithParents[]
    occasionalSlot: OccasionalSlot | undefined
    selectedSectionId: number | undefined
    calendarEvents: EventCalendar[]
    date: string
    startTime: string
    endTime: string
}): SelectOptions<number> {
    const { isRpeOrLaep, currentDaycare } = useCurrentDaycareInfos()

    const { t } = useTranslation()

    const eligibleChilds: ChildWithParents[] = childs
        .filter(
            (child) =>
                child.id === occasionalSlot?.childId || isChildInSection(child, currentDaycare.id, selectedSectionId)
        )
        .filter(
            (child) =>
                child.id === occasionalSlot?.childId ||
                !hasConflictingSlot(child, calendarEvents, date, startTime, endTime)
        )

    const primaryChilds: OptionsGroup<number> = { name: t(PriorityGroupForChildren.PRIMARY_GROUP_CHILD), options: [] }
    const secondaryChilds: OptionsGroup<number> = {
        name: t(PriorityGroupForChildren.NOT_PRIMARY_GROUP_CHILD, {
            fromSectionLabel: isRpeOrLaep ? t('fromSector') : t('fromSection')
        }),
        options: []
    }

    for (const child of eligibleChilds) {
        const isPrimarySection: boolean = child.daycares.some(
            (daycare) =>
                daycare.daycareId === currentDaycare.id &&
                daycare.sections.some((section) => section.sectionId === selectedSectionId && section.isPrimary)
        )
        const group: OptionsGroup<number> = isPrimarySection ? primaryChilds : secondaryChilds
        group.options.push(toChildSelectOption(child, currentDaycare.id, isRpeOrLaep))
    }

    primaryChilds.options.sort((a, b) => a.name.localeCompare(b.name))
    secondaryChilds.options.sort((a, b) => a.name.localeCompare(b.name))

    return [primaryChilds, secondaryChilds]
}
